import type { Bank, CreditCard } from '@eti/schema-types';
import { useProperty, type useTranslation } from '@eti/providers';
import {
  DISPATCH_ACTION,
  FUNDING,
  SCRIPT_LOADING_STATE,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { useEffect, useMemo } from 'react';
import { useScript } from 'usehooks-ts';
import {
  type PaymentMethodCode,
  paymentMethodCodes,
  paymentMethodTypes,
} from '../constants/paymentMethods';
import type {
  BankPaymentMethodOnPaymentPage,
  CardPaymentMethodOnPaymentPage,
  PayInternalPaymentMethodOnPaymentPage,
  PaymentMethodOnPaymentPage,
  TransformedPaymentMethod,
  TransformedPaymentMethodPrice,
  TransformedWalletPaymentMethod,
  WalletOnPaymentPage,
  WalletPaymentMethodOnPaymentPage,
} from '../models';
import { KLARNA } from '../payment-methods/klarna/constants';

declare const window: Window & { ApplePaySession?: { canMakePayments: () => boolean } };

const { BANK, CARD, PAY_INTERNAL, WALLET } = paymentMethodTypes;
const {
  APPLE_PAY,
  APP_CARD,
  KLARNA_ONE,
  KLARNA_PAY_LATER,
  KLARNA_PAY_NOW,
  KLARNA_SLICE_IT,
  KLARNA_SOFORT,
  PAYPAL,
  PAYPAL_PAY_LATER,
  UPI,
} = paymentMethodCodes;

export const getMethod = <T extends TransformedPaymentMethod>(
  transformedPaymentMethods: TransformedPaymentMethod[],
  code: PaymentMethodCode,
) => transformedPaymentMethods.find((method): method is T => method.code === code);

const separateWallets = (wallets: WalletOnPaymentPage[]) =>
  wallets.reduce<{
    upiWallets: WalletOnPaymentPage[];
    otherWallets: WalletOnPaymentPage[];
    inicisWallets: WalletOnPaymentPage[];
  }>(
    (acc, wallet) => {
      if (wallet.code.match(/upi/gi)) {
        return { ...acc, upiWallets: [...acc.upiWallets, wallet] };
      }
      if (wallet.code.match(/appcard/gi)) {
        return { ...acc, inicisWallets: [...acc.inicisWallets, wallet] };
      }

      return { ...acc, otherWallets: [...acc.otherWallets, wallet] };
    },
    { upiWallets: [], otherWallets: [], inicisWallets: [] },
  );

const getWallets = (wallets?: (WalletOnPaymentPage | null)[] | null): WalletOnPaymentPage[] =>
  wallets?.filter((wallet): wallet is WalletOnPaymentPage => Boolean(wallet)) ?? [];

const isBankPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is BankPaymentMethodOnPaymentPage => method.__typename === 'BankPaymentMethod';

const isWalletPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is WalletPaymentMethodOnPaymentPage => method.__typename === 'WalletPaymentMethod';

const isCardPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is CardPaymentMethodOnPaymentPage => method.__typename === 'CardPaymentMethod';

const isPayInternalPaymentMethod = (
  method: PaymentMethodOnPaymentPage,
): method is PayInternalPaymentMethodOnPaymentPage =>
  method.__typename === 'PayInternalPaymentMethod';

export const transformPaymentMethods = (paymentMethods: PaymentMethodOnPaymentPage[]) => {
  if (paymentMethods.length === 0) {
    return [];
  }
  return paymentMethods.reduce<TransformedPaymentMethod[]>((accumulatedMethods, method) => {
    if (isBankPaymentMethod(method)) {
      if (method.banks) {
        const transformedBank = method.banks
          .filter((bank): bank is Bank => Boolean(bank))
          .map((bank) => ({
            ...bank,
            paymentServiceProvider: method.paymentServiceProvider,
            texts: method.texts,
            type: BANK,
          }));

        return [...accumulatedMethods, ...transformedBank];
      }

      return accumulatedMethods;
    }

    if (isWalletPaymentMethod(method)) {
      const wallets = getWallets(method.wallets ?? []);
      const { upiWallets, otherWallets, inicisWallets } = separateWallets(wallets);
      const transformedUpiWallet =
        upiWallets.length > 0
          ? {
              code: UPI,
              name: UPI,
              type: WALLET,
              options: upiWallets.map((wallet) => ({
                type: WALLET,
                ...wallet,
              })),
              paymentServiceProvider: method.paymentServiceProvider,
            }
          : null;

      const transformedOtherWallets = otherWallets.map((wallet) => ({
        ...wallet,
        type: WALLET,
        code: wallet.code as TransformedWalletPaymentMethod['code'], // wallet.code is typed as string but should ideally be an enum.
        paymentServiceProvider: method.paymentServiceProvider,
      }));

      const transformedInicisWallets =
        inicisWallets.length > 0
          ? {
              code: APP_CARD,
              name: APP_CARD,
              type: WALLET,
              options: inicisWallets.map((wallet) => ({
                ...wallet,
                type: WALLET,
                code: wallet.code as TransformedWalletPaymentMethod['code'],
              })),
              paymentServiceProvider: method.paymentServiceProvider,
            }
          : null;

      return [
        ...accumulatedMethods,
        ...transformedOtherWallets,
        ...(transformedUpiWallet ? [transformedUpiWallet] : []),
        ...(transformedInicisWallets ? [transformedInicisWallets] : []),
      ];
    }

    if (isCardPaymentMethod(method)) {
      const creditCards =
        method.creditCards?.filter((creditCard): creditCard is CreditCard => Boolean(creditCard)) ??
        [];
      const transformedCard = {
        ...method,
        creditCards,
        code: CARD,
        type: CARD,
      };
      return [...accumulatedMethods, transformedCard];
    }

    if (isPayInternalPaymentMethod(method)) {
      const transformedPayInternal = {
        ...method,
        code: PAY_INTERNAL,
        type: PAY_INTERNAL,
      };
      return [...accumulatedMethods, transformedPayInternal];
    }

    return accumulatedMethods;
  }, []);
};

const useApplePaySDK = (shouldLoad: boolean) => {
  const applePaySDKStatus = useScript(
    shouldLoad ? 'https://applepay.cdn-apple.com/jsapi/v1.3.2/apple-pay-sdk.js' : null,
  );

  return {
    isLoading: applePaySDKStatus === 'loading',
  };
};

const usePayPalSDK = (shouldLoad: boolean) => {
  const [
    { isInitial: isPayPalSDKInitial, isPending: isPayPalSDKPending, options },
    dispatchPayPal,
  ] = usePayPalScriptReducer();

  useEffect(() => {
    if (shouldLoad && isPayPalSDKInitial && Boolean(options.clientId)) {
      dispatchPayPal({
        type: DISPATCH_ACTION.LOADING_STATUS,
        value: SCRIPT_LOADING_STATE.PENDING,
      });
    }
  }, [dispatchPayPal, isPayPalSDKInitial, shouldLoad, options.clientId]);

  return {
    isLoading: isPayPalSDKPending,
  };
};

const isPaymentMethodEligible = (paymentMethod: TransformedPaymentMethod): boolean => {
  switch (paymentMethod.code) {
    case APPLE_PAY:
      return window.ApplePaySession?.canMakePayments() ?? false;
    case PAYPAL:
      return window.paypal?.isFundingEligible?.(FUNDING.PAYPAL) ?? false;
    case PAYPAL_PAY_LATER:
      return window.paypal?.isFundingEligible?.(FUNDING.PAYLATER) ?? false;
    default:
      return true;
  }
};

export const useEligiblePaymentMethods = (paymentMethods: TransformedPaymentMethod[]) => {
  const { p } = useProperty();

  const hasApplePay = Boolean(getMethod(paymentMethods, APPLE_PAY));
  const hasPayPal = Boolean(getMethod(paymentMethods, PAYPAL));
  const hasPayPalPayLater = Boolean(getMethod(paymentMethods, PAYPAL_PAY_LATER));

  const { isLoading: applePayIsLoading } = useApplePaySDK(
    hasApplePay && p('Payment.Method.ApplePay.SDK.Enabled'),
  );
  const { isLoading: payPalIsLoading } = usePayPalSDK(hasPayPal || hasPayPalPayLater);

  const loadingConditions = useMemo(
    () => [
      {
        enabled: hasApplePay,
        isLoading: applePayIsLoading,
      },
      {
        enabled: hasPayPal || hasPayPalPayLater,
        isLoading: payPalIsLoading,
      },
    ],
    [hasApplePay, hasPayPal, hasPayPalPayLater, applePayIsLoading, payPalIsLoading],
  );

  const isLoading = loadingConditions.some((condition) => condition.enabled && condition.isLoading);

  const eligiblePaymentMethods = paymentMethods.filter((paymentMethod) => {
    try {
      return isPaymentMethodEligible(paymentMethod);
    } catch {
      return false;
    }
  });

  return {
    isLoading,
    eligiblePaymentMethods,
  };
};

export const sortPaymentMethods = (
  paymentMethods: TransformedPaymentMethod[],
  displayOrder?: string,
) => {
  if (!displayOrder) {
    return paymentMethods;
  }

  const order = displayOrder
    .split(',')
    .filter(Boolean)
    .map((code) => code.trim());

  return paymentMethods.sort((a, b) => {
    if (order.indexOf(a.code) === -1) {
      return 1;
    }
    if (order.indexOf(b.code) === -1) {
      return -1;
    }
    return order.indexOf(a.code) - order.indexOf(b.code);
  });
};

export const getPaymentMethodPrices = (
  paymentMethods: TransformedPaymentMethod[],
  t: ReturnType<typeof useTranslation>['t'],
) =>
  paymentMethods.reduce((acc: TransformedPaymentMethodPrice[], method) => {
    if (method.type === CARD) {
      return [
        ...acc,
        ...method.creditCards.map((card) => ({
          id: card.code,
          name: card.name,
          price: card.price.value,
          serviceFee: card.serviceFee?.value,
          type: method.type,
        })),
      ];
    }

    if (method.type === WALLET && (method.code === UPI || method.code === APP_CARD)) {
      return [
        ...acc,
        ...method.options.map((option) => ({
          id: option.code,
          name: t(`Payment.Method.${option.code}.Text`) || option.name,
          price: option.price.value,
          serviceFee: option.serviceFee?.value,
          type: method.type,
        })),
      ];
    }

    if (method.type === PAY_INTERNAL) {
      return [...acc];
    }

    return [
      ...acc,
      {
        id: method.code,
        name: method.name,
        price: method.price.value,
        serviceFee: method.serviceFee?.value,
        type: method.type,
      },
    ];
  }, []);

export const getPaymentMethodLogoId = (
  code: PaymentMethodCode,
  shouldShowBancontactLogo: boolean = false,
) => {
  switch (code) {
    case CARD:
      return ['logo_VI', 'logo_CA', ...(shouldShowBancontactLogo ? ['logo_BT'] : [])];
    case KLARNA_ONE:
    case KLARNA_PAY_NOW:
    case KLARNA_PAY_LATER:
    case KLARNA_SLICE_IT:
    case KLARNA_SOFORT:
      return KLARNA;
    case PAYPAL:
    case PAYPAL_PAY_LATER:
      return PAYPAL.toLowerCase();
    default:
      return code.toLowerCase();
  }
};
